import {
  DocumentNode,
  SubscriptionHookOptions,
  TypedDocumentNode,
  // eslint-disable-next-line @typescript-eslint/no-restricted-imports
  useSubscription as useApolloSubscription,
} from '@apollo/client';
import { RefetchFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import { useRef } from 'react';
import { ConnectionStatusType } from '@/components/connection-status/connection-status-type.ts';
import { traceError } from '@/utils/trace-error.ts';

export const useSubscription = (
  subscription: DocumentNode | TypedDocumentNode,
  options?: SubscriptionHookOptions | undefined,
  setSubscriptionStatus?: (subscriptionStatus: ConnectionStatusType) => void,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchQuery?: RefetchFunction<any, Record<string, unknown>>,
  retries = 10,
) => {
  const restartCount = useRef(0);

  const { restart } = useApolloSubscription(subscription, {
    ...options,
    onData: async (data) => {
      const error = data?.data?.error;

      // additional error handling, because received data with errors doesn't trigger error method
      if (error) {
        setSubscriptionStatus?.(ConnectionStatusType.Connecting);

        traceError(
          new Error('[Subscription error]: ' + error.message, {
            cause: error,
          }),
        );

        if (refetchQuery) await refetchQuery();
        setSubscriptionStatus?.(ConnectionStatusType.Connected);

        return;
      }

      options?.onData?.(data);
    },
    onError: async (error) => {
      const retriesExceeded = restartCount.current >= retries;

      const errorMessage = error.message;

      setSubscriptionStatus?.(ConnectionStatusType.Connecting);
      if (refetchQuery) await refetchQuery();
      restartCount.current += 1;

      traceError(
        new Error('[Subscription error]: ' + errorMessage, {
          cause: error,
        }),
      );

      // stop trying to reconnect, when retries exceeded
      if (retriesExceeded) {
        setSubscriptionStatus?.(ConnectionStatusType.Disconnected);

        return;
      }

      // re-subscribing
      setSubscriptionStatus?.(ConnectionStatusType.Connected);
      restart();

      options?.onError?.(error);
    },
  });
};
